import get from "lodash/get";
import isEqual from "lodash/isEqual";
import {
    IAllocationDimension,
    AllocationDimensionKind,
    getAllocationDimensionByIndex,
    IAllocationDimensionValue,
    AllocationDimensionIndex,
    AllocationDimensionFields,
    BagValues,
    BAG_VALUE_PARAMS_SEPARATOR,
} from "helpers/allocation-dimension";
import { IBagTable } from "helpers/table-bag";
import { ISchemaKey } from "./product-collection";
import { SizingErrors } from "constants/product-collection";
import { GROUP_ID_DELIMITER, IDoor } from "./door-collection";
import { Set } from "immutable";

/**
 *
 * @param data Content of bag
 * @param allocationDimensions List of generic dimensions used within collection
 * @param hasGlobalGroup In a case where there is more than one dimension, eg: Delivery & Door, flag whether there is root dimension, eg: Delivery
 * @param isAllocation Flag whether there is size-level quantity
 * @param groupedByDoor Flag whether total should be grouped per lowest dimension key (Eg: door)
 * @returns Sum of quantity. If `hasGlobalGroup` is true, sum of quantity grouped by root dimension (eg: delivery)
 */

type SizeTotals = Record<string, number | Record<string, number>>;
const calculateSizeTotalsLegacy = (
    data: any[],
    allocationDimensions: IAllocationDimension[],
    hasGlobalGroup: boolean = true,
    isAllocation: boolean = false,
    groupedByDoor: boolean = true
): SizeTotals => {
    const doors: IAllocationDimensionValue[] = get(
        getAllocationDimensionByIndex(allocationDimensions, AllocationDimensionIndex.Door) || {},
        AllocationDimensionFields.dimensionValues
    );
    const dates: IAllocationDimensionValue[] = get(
        getAllocationDimensionByIndex(allocationDimensions, AllocationDimensionIndex.Date) || {},
        AllocationDimensionFields.dimensionValues
    );

    const seenKeysSet = Set<string>();

    // Suppose there are repeated entries on the table with path P
    // { r: [...,
    //      {c : [...P, "1"]},
    //      {c : [...P, "2"]},
    //      {c : [...P, "6"]},
    //      ...
    // ]}
    // Getter logic will report, for any use case, the last value ("6" in this example).
    // This is why we need to use reduceRight; to operate on the last entry first
    return data.reduceRight(
        (acc, cell) => {
            const [cellValue, ...restItems] = [...cell.c].reverse();

            // Logic to prevent adding repeated entries more than once
            const path = restItems.join(GROUP_ID_DELIMITER);
            if (seenKeysSet.has(path)) {
                return acc;
            }
            seenKeysSet.add(path);

            let groupedKey = "";
            if (isAllocation) {
                const [sizeSelected] = restItems.splice(0, 1);
                groupedKey = !groupedByDoor ? sizeSelected : "";
            }

            const [doorId, month] = restItems;
            const isDateDimensionAvailable = hasGlobalGroup
                ? dates?.some((date) => date.id === month && date.active && date.included)
                : true;
            const isDoorDimensionAvailable = doors?.some((door) => door.id === doorId && door.active && door.included);
            const isZeroOrEmpty = Boolean(Number(cellValue)) === false;
            if (!isDoorDimensionAvailable || !isDateDimensionAvailable || isZeroOrEmpty) {
                return acc;
            }

            groupedKey = groupedKey || doorId;
            let totalValue = get(acc, [groupedKey, ...(hasGlobalGroup ? [month] : [])], 0);
            totalValue += Number(cellValue);

            if (!hasGlobalGroup) {
                acc[groupedKey] = totalValue;
            } else {
                const grouped = get(acc, [groupedKey], {});
                grouped[month] = totalValue;
                acc[groupedKey] = grouped;
            }

            return acc;
        },
        {} as Record<PropertyKey, number | Record<PropertyKey, number>>
    );
};

/**
 *
 * @param bagValues Content of bag
 * @param allocationDimensions List of generic dimensions used within collection
 * @param isAllocation Flag whether there is size-level quantity
 * @param groupedByDoor Flag whether total should be grouped per lowest dimension key (Eg: door)
 * @returns Sum of quantity. If `hasGlobalGroup` is true, sum of quantity grouped by root dimension (eg: delivery)
 */
const calculateSizeTotals = (
    bagValues: BagValues,
    allocationDimensions: IAllocationDimension[],
    isAllocation: boolean = false,
    groupedByDoor: boolean = true
): SizeTotals => {
    const genericAllocationDimensions = allocationDimensions.filter(
        (allocationDimension) => allocationDimension.subtype === AllocationDimensionKind.Generic
    );
    const isSingleDimension = genericAllocationDimensions.length === 1;
    const doors: IAllocationDimensionValue[] = get(
        genericAllocationDimensions[genericAllocationDimensions.length - 1],
        AllocationDimensionFields.dimensionValues
    );
    const dates: IAllocationDimensionValue[] = get(
        genericAllocationDimensions.length > 1 ? genericAllocationDimensions[0] : {},
        AllocationDimensionFields.dimensionValues
    );

    return bagValues.reduce((acc, value, key) => {
        const quantity = value?.get("string_value") ?? "0";
        const dimensionValueIds = key.split(BAG_VALUE_PARAMS_SEPARATOR);

        let groupedKey = "";
        if (isAllocation) {
            const sizeSelected = dimensionValueIds.pop();
            groupedKey = !groupedByDoor ? sizeSelected : "";
        }

        const month = dimensionValueIds.length === 1 ? undefined : dimensionValueIds[0];
        const doorId =
            dimensionValueIds.length === 1 ? dimensionValueIds[0] : dimensionValueIds[dimensionValueIds.length - 1];
        const isDateDimensionAvailable = !isSingleDimension
            ? dates?.some((date) => date.id === month && date.active && date.included)
            : true;
        const isDoorDimensionAvailable = doors?.some((door) => door.id === doorId && door.active && door.included);
        const isZeroOrEmpty = Boolean(Number(quantity)) === false;
        if (!isDoorDimensionAvailable || !isDateDimensionAvailable || isZeroOrEmpty) {
            return acc;
        }
        groupedKey = groupedKey || doorId;
        let totalValue = get(acc, [groupedKey, ...(!isSingleDimension ? [month] : [])], 0);
        totalValue += Number(quantity);

        if (isSingleDimension) {
            acc[groupedKey] = totalValue;
        } else {
            const grouped = get(acc, [groupedKey], {});
            grouped[month] = totalValue;
            acc[groupedKey] = grouped;
        }

        return acc;
    }, {} as SizeTotals);
};

// Check if one of the sizes have quantity. `false` when sizes is empty or none of it has quantity.
export const doSizesHaveQuantityLegacy = (
    sizes: string[],
    bag: IBagTable,
    allocationDimensions: IAllocationDimension[]
): boolean => {
    if (!sizes.length) {
        return false;
    }

    const genericAllocationDimensions = allocationDimensions.filter(
        (allocationDimension) => allocationDimension.subtype === AllocationDimensionKind.Generic
    );
    const hasGlobalGroup = genericAllocationDimensions.length > 1;
    const doorsTotal = calculateSizeTotalsLegacy(get(bag, "r", []), allocationDimensions, hasGlobalGroup, true, false);

    return sizes.some((size) => {
        const sizeAdded = doorsTotal[size];
        if (typeof sizeAdded === "object") {
            return Object.values(sizeAdded).some((it) => it > 0);
        }
        return sizeAdded > 0;
    });
};

/**
 * Check if one of the sizes have quantity. `false` when sizes is empty or none of it has quantity.
 * @param sizes List sizes to check
 * @param bagValues Content of the bag
 * @param allocationDimensions List of dimensions used within collection
 * @returns
 */
export const doSizesHaveQuantity = (
    sizes: string[],
    bagValues: BagValues,
    allocationDimensions: IAllocationDimension[]
): boolean => {
    if (!sizes.length) {
        return false;
    }

    const totals = calculateSizeTotals(bagValues, allocationDimensions, true, false);

    return sizes.some((size) => {
        const sizeAdded = totals[size];
        if (typeof sizeAdded === "object") {
            return Object.values(sizeAdded).some((it) => it > 0);
        }
        return sizeAdded > 0;
    });
};

export const doesBulksAndAllocationsMatchLegacy = (
    allocations: any,
    bulks: any,
    allocationDimensions: IAllocationDimension[]
): boolean => {
    const genericAllocationDimensions = allocationDimensions.filter(
        (allocationDimension) => allocationDimension.subtype === AllocationDimensionKind.Generic
    );
    const hasGlobalGroup = genericAllocationDimensions.length > 1;
    const doorsTotal = calculateSizeTotalsLegacy(get(allocations, "r", []), allocationDimensions, hasGlobalGroup, true);
    const bulksTotal = calculateSizeTotalsLegacy(get(bulks, "r", []), allocationDimensions, hasGlobalGroup);

    return isEqual(doorsTotal, bulksTotal);
};

export const doesBulksAndAllocationsMatch = (
    allocations: BagValues,
    bulks: BagValues,
    allocationDimensions: IAllocationDimension[]
) => {
    const doorsTotal = calculateSizeTotals(allocations, allocationDimensions, true);
    const bulksTotal = calculateSizeTotals(bulks, allocationDimensions);

    return isEqual(doorsTotal, bulksTotal);
};

export const getSizingErrorStatus = (
    sizingFieldsData: { [fieldName: string]: string },
    isEmptyAllocation: boolean,
    sizingFields: { sizeCurveSchema: ISchemaKey; selectedSizePacksSchema?: ISchemaKey }
): SizingErrors | undefined => {
    const { selectedSizePacksSchema, sizeCurveSchema } = sizingFields;
    if (!sizingFieldsData[sizeCurveSchema.field_name]) {
        // If no pre-selected Size Curve
        return SizingErrors.CurveNotFound;
    } else if (selectedSizePacksSchema && sizingFieldsData[selectedSizePacksSchema.field_name]) {
        if (isEmptyAllocation) {
            return SizingErrors.PackConstraintsInfeasible;
        } else {
            return SizingErrors.BulkPackConstraintsMismatch;
        }
    } else if (isEmptyAllocation) {
        return SizingErrors.CurveContainsZeroUnits;
    }
};

export const getNumberOfDoors = (isAverageUnitsMode: boolean, door?: IDoor) => {
    if (!door) {
        return 0;
    }

    if (isAverageUnitsMode) {
        return door.groupedDoorsNumber ?? 0;
    }

    return 1;
};
